/* ============================================================
   The small set of primitives every page is built from. Deliberately plain:
   the pages own their layout, these own the look of a control — and the two
   page frames (`Page`, `PageHeader`) that keep every screen's column and
   title in one voice.
   ============================================================ */
import React from 'react';
import type { HueName } from '@habishine/client-core';

import { hueVar } from '../theme/hues';
import { SunMark } from './SunMark';

/* The centred column every page's content sits in. Pages that paint
   full-bleed (Today's sky) use it only below the painted part. */
export function Page({ children, className = '', wide }: { children: React.ReactNode; className?: string; wide?: boolean }) {
  return <div className={`mx-auto w-full max-w-[920px] px-4 md:px-8 ${wide ? 'xl:max-w-[1200px]' : 'xl:max-w-[1200px]'} ${className}`}>{children}</div>;
}

/* A page's opening: a small eyebrow, the title in the display face, an
   optional line under it, actions on the right. `hue` lights a soft radial
   wash behind the title, so Plan, Review and a project each have a weather. */
export function PageHeader({
  eyebrow, title, sub, right, hue, titleAccessory, className = '',
}: {
  eyebrow?: React.ReactNode; title: React.ReactNode; sub?: React.ReactNode; right?: React.ReactNode; hue?: HueName | 'frog'; titleAccessory?: React.ReactNode; className?: string;
}) {
  const glow = hue === 'frog' ? 'var(--frog-wash)' : hue ? `color-mix(in srgb, ${hueVar(hue, 'c')} 16%, transparent)` : 'transparent';
  return (
    <header className={`relative mb-5 flex flex-col gap-3 pt-6 md:mb-6 md:flex-row md:items-end md:gap-4 md:pt-10 ${className}`}>
      <div className="pointer-events-none absolute -left-24 -top-10 h-[260px] w-[520px]" style={{ background: `radial-gradient(closest-side, ${glow}, transparent)` }} aria-hidden />
      <div className="relative min-w-0 flex-1">
        {eyebrow ? <div className="mb-1.5 text-[12px] font-bold uppercase tracking-[1.4px] text-frog">{eyebrow}</div> : null}
        <div className="flex items-center gap-3">
          {titleAccessory}
          <h1 className="font-display truncate text-[34px] font-semibold leading-[1.02] text-ink md:text-[44px]">{title}</h1>
        </div>
        {sub ? <p className="mt-2 max-w-[56ch] text-[14.5px] leading-[1.5] text-meta">{sub}</p> : null}
      </div>
      {right ? <div className="relative flex shrink-0 flex-wrap items-center gap-2 md:pb-1.5">{right}</div> : null}
    </header>
  );
}

/* The loading states: a sun that breathes while the session is restored or the
   day loads. The first thing a returning user sees, so it is the mark, not a
   spinner. */
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 text-meta" role="status">
      <span className="sun-glow inline-block"><SunMark size={44} /></span>
      <span className="text-[13.5px] font-medium">{label}</span>
    </div>
  );
}

export function Button({
  variant = 'secondary',
  busy,
  size = 'md',
  className = '',
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  busy?: boolean;
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-[transform,box-shadow,background-color,filter] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-frog-wash active:scale-[0.98]';
  const dims = size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-4.5 py-2.5 text-[14px]';
  const look = {
    primary: 'frog-gradient text-white hover:brightness-[1.06]',
    secondary: 'bg-card-sunk text-ink hover:bg-hairline',
    ghost: 'text-frog hover:bg-frog-wash',
    danger: 'text-replan hover:bg-nudge',
  }[variant];
  return (
    <button className={`${base} ${dims} ${look} ${className}`} disabled={busy || rest.disabled} {...rest}>
      {busy ? (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : null}
      {children}
    </button>
  );
}

export const inputClass =
  'w-full rounded-tile border border-hairline bg-card px-3.5 py-2.5 text-[15px] text-ink outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-placeholder focus:border-frog focus:shadow-[0_0_0_4px_var(--frog-wash)]';

export function Field({
  label,
  className = '',
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[1px] text-meta">{label}</span>
      <input className={inputClass} {...rest} />
    </label>
  );
}

/* A pill group where exactly one is on — appearance, week start, ranges. */
export function Segmented<T extends string | number>({
  value, onChange, options, className = '',
}: {
  value: T; onChange: (v: T) => void; options: { value: T; label: React.ReactNode; title?: string }[]; className?: string;
}) {
  return (
    <div className={`inline-flex gap-0.5 rounded-[22px] bg-card-sunk p-1 ${className}`} role="radiogroup">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={on}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all duration-150 ${on ? 'bg-card text-ink shadow-[0_1px_2px_rgba(20,20,25,0.08),0_4px_12px_-4px_rgba(20,20,25,0.14)]' : 'text-meta hover:text-ink'}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function HueDot({ hue, size = 10 }: { hue: HueName; size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, background: hueVar(hue, 'c') }}
    />
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-row bg-card-sunk px-4 py-3 text-sm text-meta">{children}</p>;
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`surface p-5 ${className}`}>{children}</div>;
}

/* a card's title row: the heading face, optional right slot */
export function CardTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="font-head text-ink">{children}</h2>
      {right}
    </div>
  );
}

/* the account's one-letter avatar, in the brand gradient */
export function Avatar({ email, size = 28 }: { email?: string | null; size?: number }) {
  const letter = (email ?? '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="sun-gradient inline-flex shrink-0 items-center justify-center rounded-full font-display text-white" style={{ width: size, height: size, fontSize: size * 0.5, fontWeight: 600 }} aria-hidden>
      {letter}
    </span>
  );
}
